import {useSelector} from 'react-redux'

import {selectUser} from 'features/User'
import {DOPE_PRIMARY_DOMAIN} from 'definitions/constant'
import {EnvironmentEnum} from 'interfaces'
import {useEntitlementsQuery} from 'features/Entitlements/hooks'
import {selectSSLInspectionDisabledInData} from 'features/Policies/policiesSelectors'
import {useSSLInspectionRelease} from './useSSLInspectionRelease'

/** O365 Copilot controls are hidden for these tenant primary domains. */
const COPILOT_CONTROLS_HIDDEN_PRIMARY_DOMAINS = new Set([
  'tjx.com',
  'havebury.com',
  'successacademies.org',
  'monolithicpower.com',
  'mpsmail.onmicrosoft.com',
])

interface UseFeatureFlagReturnType {
  isDopeTenant: boolean
  isPaywalled: boolean
  developmentOnlyFeatureFlag: boolean
  testOnlyFeatureFlag: boolean
  stagingOnlyFeatureFlag: boolean
  deploymentOnlyFeatureFlag: boolean
  nonProductionFeatureFlag: boolean
  dopeInternalFeatureFlag: boolean
  productionDopeFeatureFlag: boolean
  swgDlpBlock: boolean
  isSSLInspectionToggleFeatureFlag: boolean
  isSSLInspectionDisabled: boolean
  // SSPM feature flags
  hasSSPMEarlyAccess: boolean
  isNewSSPMTableViewEnabled: boolean
  isSSPMPermissionsFilterEnabled: boolean
  /** Tenant gate for O365 Copilot CAC controls (combine with O365 app selection in UI). */
  isO365CopilotControlsEnabledForTenant: boolean
}

/**
 * @description This hook is used to get the feature flag values
 */
export const useFeatureFlag = (): UseFeatureFlagReturnType => {
  const user = useSelector(selectUser)
  const {isSSLInspectionToggleReleased} = useSSLInspectionRelease()

  const isSSLInspectionDisabled = useSelector(selectSSLInspectionDisabledInData)
  const {casbFullAccess, swgDlpBlock, sspmEarlyAccess, isLoading} =
    useEntitlementsQuery({
      enabled: false,
    })
  const isPaywalled = !casbFullAccess && !isLoading

  const primaryDomain = user?.primaryDomain

  /**
   * Default global feature flags for the application.
   */
  const isDopeTenant = primaryDomain === DOPE_PRIMARY_DOMAIN

  const developmentOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.DEVELOPMENT

  const testOnlyFeatureFlag = process.env.REACT_APP_ENV === EnvironmentEnum.TEST

  const deploymentOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.DEPLOYMENT

  const stagingOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.STAGING

  const nonProductionFeatureFlag =
    process.env.REACT_APP_ENV !== EnvironmentEnum.PRODUCTION

  // true if the environment is not production and primaryDomain is dope.security
  const dopeInternalFeatureFlag = nonProductionFeatureFlag && isDopeTenant

  // true if the environment is production and primaryDomain is dope.security
  const productionDopeFeatureFlag = !nonProductionFeatureFlag && isDopeTenant

  /**
   * Individual feature flags for different features on the platform.
   */

  // SSPM feature flags

  // Decides whether SSPM toggle is disabled on CASB DLP pages.
  const hasSSPMEarlyAccess: boolean = sspmEarlyAccess
  // Decides whether the new SSPM table designs are available.
  const isNewSSPMTableViewEnabled: boolean = hasSSPMEarlyAccess
  // Decides whether the permissions filter is available on the new SSPM table (future work on BE)
  const isSSPMPermissionsFilterEnabled: boolean = developmentOnlyFeatureFlag

  // SSL Inspection toggle feature flag
  const isSSLInspectionToggleFeatureFlag =
    nonProductionFeatureFlag || isSSLInspectionToggleReleased

  const isO365CopilotControlsEnabledForTenant =
    !COPILOT_CONTROLS_HIDDEN_PRIMARY_DOMAINS.has(
      (primaryDomain ?? '').toLowerCase(),
    )

  return {
    nonProductionFeatureFlag,
    isDopeTenant,
    developmentOnlyFeatureFlag,
    deploymentOnlyFeatureFlag,
    testOnlyFeatureFlag,
    stagingOnlyFeatureFlag,
    dopeInternalFeatureFlag,
    productionDopeFeatureFlag,
    isPaywalled,
    swgDlpBlock,
    isSSLInspectionToggleFeatureFlag,
    isSSLInspectionDisabled:
      isSSLInspectionToggleFeatureFlag && isSSLInspectionDisabled,
    hasSSPMEarlyAccess,
    isNewSSPMTableViewEnabled,
    isSSPMPermissionsFilterEnabled,
    isO365CopilotControlsEnabledForTenant,
  }
}
